export type ChatCommandName =
  | 'history'
  | 'query'
  | 'explain'
  | 'stop'
  | 'chat_users'
  | 'defaults'

export type ChatResponseStatus =
  | 'starting'
  | 'in-progress'
  | 'response-delayed'
  | 'complete'

export enum ChatSubStatus {
  AnalyzingDocument = 'analyzing-document',
  ProcessingChunks = 'processing-chunks',
  ProcessingCitations = 'processing-citations',
}

export type ChatModel = {
  name: string
  default: boolean
  id: string
}

export type ChatModelProvider = {
  id: string
  name: string
  default: boolean
  models: ChatModel[]
}

export type ChatBound = {
  left: number
  top: number
  width: number
  height: number
}

export type ChatCitationLine = {
  id: number
  bounds: ChatBound
  text: string
  page: number
}

export type ChatCitationParagraph = {
  id: number
  score: number
  text: string
  bounds: ChatBound
  lines: ChatCitationLine[]
}

export type ChatCitationDocument = {
  uuid: string
  name: string
  guid: string
}

export type ChatProvisionLine = {
  page: number
  bounds: ChatBound
  text: string
}

export type ChatProvision = {
  id: string
  record_id: number
  provision_id: number
  bounds: ChatBound | null
  text: string
  page: number
  score: number
  lines: ChatProvisionLine[]
}

export type ChatCitation = {
  page?: number
  paragraphs: ChatCitationParagraph[]
  document?: ChatCitationDocument
  provisions: ChatProvision[]
}

export type ChatCitationMap = Record<number, ChatCitation>

export type ChatSettings = {
  system: string
  model: string
  provider: string
  top_p: number
  temperature: number
  provisions: number[]
  context_mode: string
  model_provider_map: ChatModelProvider[]
  context_mode_map: Record<string, string>
  command: ChatCommandName
  context: string
  prompt: string
  with_citations?: boolean
  citations?: {
    sources: ChatCitationMap
  }
}

export type ChatHistoryItemData = ChatSettings & {
  command: ChatCommandName
  context: string
  prompt: string
}

export type ChatHistoryItem = {
  speaker: string
  chat_text: string
  model: string
  data: ChatHistoryItemData | {}
  created_at: string
}

export type ChatUser = {
  id: number
  name: string
  email: string
  offices_count: number
  offices: {
    id: number
    uuid: string
    ref_id: string
    name: string
    is_current: boolean
    is_internal: boolean
    is_default: boolean
    is_upload_allowed: boolean
    use_sso: boolean
    use_word: boolean
    use_assist: boolean
    use_procore: boolean
    customer_id: string
    is_trial: boolean
    is_partner: boolean
  }[]
  job_title: string
  role: string
  phone: string
  last_login: string
  is_active: boolean
  use_sso: boolean
  current_customer_id: string
  uuid: string
}

export type ChatResponseErrorItem = {
  error_id: string
  error_message: string
}

export type ChatRequestContext = {
  api_token: string
  uuid: string
  user_id?: number
  doc_guid?: string
  project_uuid?: string
}

export type ChatTextReference = {
  pageIndex: number
  rects: ChatBound[]
}

export interface ChatRequest {
  command: ChatCommandName
  context: ChatRequestContext
  payload: unknown
}

export interface ChatResponse {
  command: ChatCommandName
  payload: unknown
  errors?: ChatResponseErrorItem[]
}

export interface ChatDefaultsRequest extends ChatRequest {
  command: 'defaults'
  payload: {
    settings: {}
  }
}

export interface ChatDefaultsResponse extends ChatResponse {
  command: 'defaults'
  payload: ChatSettings
}

export interface ChatHistoryRequest extends ChatRequest {
  command: 'history'
  payload: {
    limit?: number
    offset?: number
  }
}

export type ChatHistoryResponseItem = {
  chat_item_id: string
  timestamp: string
  command: ChatCommandName
  references: ChatTextReference[]
  reference_text: string
  human: ChatHistoryItem
  robot: ChatHistoryItem
  system: ChatHistoryItem | ''
  context: ChatHistoryItem | ''
  citations?: {
    sources: ChatCitationMap
  }
}

export interface ChatHistoryResponse extends ChatResponse {
  command: 'history'
  payload:
    | ChatHistoryResponseItem[]
    | {
        items: ChatHistoryResponseItem[]
        pagination?: {
          total: number
          offset: number
          limit: number
          has_more: boolean
        }
      }
}

export interface ChatUsersRequest extends ChatRequest {
  command: 'chat_users'
  payload: {}
}

export interface ChatUsersResponse extends ChatResponse {
  command: 'chat_users'
  payload: ChatUser[]
}

export interface ChatQueryRequest extends ChatRequest {
  command: 'query'
  payload: {
    text: string
    doc_guid: string
    settings?: ChatSettings
  }
}

export interface ChatQueryResponse extends ChatResponse {
  command: 'query'
  payload: {
    status: ChatResponseStatus
    message: string
    chat_item_id?: string
    settings?: ChatSettings
    sub_status?: ChatSubStatus
    citations?: {
      sources: ChatCitationMap
    }
  }
}

export interface ChatExplainRequest extends ChatRequest {
  command: 'explain'
  payload: {
    text: string
    references?: ChatTextReference[]
  }
}

export interface ChatExplainResponse extends ChatResponse {
  command: 'explain'
  payload: {
    status: ChatResponseStatus
    message: string
    references: ChatTextReference[]
    reference_text: string
    chat_item_id?: string
    settings?: ChatSettings
    sub_status?: ChatSubStatus
    citations?: {
      sources: ChatCitationMap
    }
  }
}

export interface ChatStopRequest extends ChatRequest {
  command: 'stop'
  payload: {}
}

export enum ChatServiceState {
  UNAVAILABLE = 'UNAVAILABLE',
  BUSY = 'BUSY',
  OK = 'OK',
}

export enum ChatErrorType {
  MissingText = 'MissingText',
  MissingDocKiraId = 'MissingDocKiraId',
  MissingDocGUID = 'MissingDocGUID',
  MissingAPIToken = 'MissingAPIToken',
  MissingUserId = 'MissingUserId',
  MissingConvoSettings = 'MissingConvoSettings',
  DocumentNotIndexed = 'DocumentNotIndexed',
  DocIdNotFound = 'DocIdNotFound',
  QueryMaxRetriesExceeded = 'QueryMaxRetriesExceeded',
  UnknownCommand = 'UnknownCommand',
  UnknownError = 'UnknownError',
  SendingError = 'SendingError',
  Default = 'Default',
}

export type ChatError = {
  type: ChatErrorType
  message: string
}

export type ChatMessage = {
  renderId: string
  type: 'robot' | 'human' | 'explain'
  text: string
  settings: ChatSettings | {}
  chat_item_id?: string
  references?: ChatTextReference[]
  reference_text?: string
  completed?: boolean
  sub_status?: ChatSubStatus
  citations?: ChatCitationMap
}
